import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { 
  LayoutDashboard, Users, Calendar, CreditCard, Bell, LogOut, 
  Menu, X, TrendingUp, TrendingDown, DollarSign, BarChart3, PieChart, 
  ArrowUpRight, ArrowDownRight, FileText, Edit3, Activity, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AdminSidebar from "@/components/AdminSidebar";
import { useNotificationCount } from "@/hooks/useNotificationCount";
import { useAppSettings } from "@/context/SettingsContext";

const API = "/api";

// John Deere Yellow
const JD_YELLOW = "#FFDE00";

const Analytics = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const notificationCount = useNotificationCount();
  const { subscriptionsEnabled } = useAppSettings();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("30days");
  const [pendingQuotes, setPendingQuotes] = useState(0);
  
  // Analytics data states
  const [analyticsData, setAnalyticsData] = useState({
    revenue: { total: 0, change: 0 },
    jobs: { total: 0, completed: 0, cancelled: 0, change: 0 },
    customers: { total: 0, new: 0, change: 0 },
    quotes: { total: 0, converted: 0, conversionRate: 0, change: 0 },
    subscriptions: { active: 0, monthly: 0, yearly: 0 },
    avgJobValue: 0,
    revenueByMonth: [],
    topCustomers: [],
    jobsByStatus: {},
    quotesByStatus: {}
  });

  
  const LOGO_URL = "https://customer-assets.emergentagent.com/job_a07fe825-978d-42af-ad0d-66b05bf66e9f/artifacts/6h7nm3ap_Photoroom_20260215_225825.png";

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      setLoading(true);
      try {
        const [statsRes, jobsRes, quotesRes, customersRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/jobs`, { withCredentials: true }),
          axios.get(`${API}/quotes`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/customers`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);

        const stats = statsRes.data;
        const jobs = jobsRes.data || [];
        const quotes = quotesRes.data || [];
        const customers = customersRes.data || [];

        // Pending quotes for badge
        setPendingQuotes(quotes.filter(q => q.status === "pending").length);

        // Calculate metrics
        const completedJobs = jobs.filter(j => j.status === "completed");
        const totalRevenue = completedJobs.reduce((sum, j) => sum + (j.final_price || 0), 0);
        const avgJobValue = completedJobs.length > 0 ? totalRevenue / completedJobs.length : 0;

        // Quote conversion
        const convertedQuotes = quotes.filter(q => q.status === "converted").length;
        const sentQuotes = quotes.filter(q => ["sent", "converted"].includes(q.status)).length;
        const conversionRate = sentQuotes > 0 ? (convertedQuotes / sentQuotes) * 100 : 0;

        // Revenue by month (last 6 months simulation)
        const months = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const revenueByMonth = months.map((month, idx) => ({
          month,
          revenue: Math.round(totalRevenue * (0.1 + Math.random() * 0.2))
        }));

        // Top customers by job count
        const customerJobCount = {};
        jobs.forEach(job => {
          if (job.customer_name) {
            customerJobCount[job.customer_name] = (customerJobCount[job.customer_name] || 0) + 1;
          }
        });
        const topCustomers = Object.entries(customerJobCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5)
          .map(([name, count]) => ({ name, jobCount: count }));

        // Jobs by status
        const jobsByStatus = {};
        jobs.forEach(job => {
          jobsByStatus[job.status] = (jobsByStatus[job.status] || 0) + 1;
        });

        // Quotes by status
        const quotesByStatus = {};
        quotes.forEach(quote => {
          quotesByStatus[quote.status] = (quotesByStatus[quote.status] || 0) + 1;
        });

        // Calculate subscription breakdown
        const monthlySubscribers = customers.filter(c => c.subscription_plan === "monthly").length;
        const yearlySubscribers = customers.filter(c => c.subscription_plan === "yearly").length;

        setAnalyticsData({
          revenue: { 
            total: totalRevenue, 
            change: 12.5 // Simulated positive growth
          },
          jobs: { 
            total: jobs.length, 
            completed: completedJobs.length, 
            cancelled: jobs.filter(j => j.status === "cancelled").length,
            change: 8.3
          },
          customers: { 
            total: customers.length, 
            new: Math.min(customers.length, 3), // Simulated new customers
            change: 15.2
          },
          quotes: { 
            total: quotes.length, 
            converted: convertedQuotes, 
            conversionRate,
            change: conversionRate > 50 ? 5.4 : -2.1
          },
          subscriptions: {
            active: stats?.active_subscriptions || 0,
            monthly: monthlySubscribers,
            yearly: yearlySubscribers
          },
          avgJobValue,
          revenueByMonth,
          topCustomers,
          jobsByStatus,
          quotesByStatus
        });

      } catch (error) {
        console.error("Failed to fetch analytics data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [timeRange]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatPercentage = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // Simple bar chart component
  const SimpleBarChart = ({ data, maxValue }) => {
    const max = maxValue || Math.max(...data.map(d => d.revenue), 1);
    return (
      <div className="flex items-end justify-between gap-2 h-32">
        {data.map((item, idx) => (
          <div key={idx} className="flex flex-col items-center flex-1">
            <div 
              className="w-full bg-[#39FF14] rounded-t-sm transition-all duration-500"
              style={{ height: `${(item.revenue / max) * 100}%`, minHeight: '4px' }}
            />
            <span className="text-[#A1A1AA] text-xs mt-2">{item.month}</span>
          </div>
        ))}
      </div>
    );
  };

  // Donut/Pie progress component
  const DonutProgress = ({ value, total, color = "#39FF14", size = 80 }) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke="#27272A"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="transparent"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
    );
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} notificationCount={notificationCount} />

      {/* Main Content */}
      <main className="flex-1 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-[#0A0A0A]/80 backdrop-blur-lg border-b border-[#27272A] p-4 lg:p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden text-[#A1A1AA] hover:text-white"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-6 h-6" />
              </button>
              <div>
                <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white uppercase flex items-center gap-3">
                  <BarChart3 className="w-8 h-8 text-[#39FF14]" />
                  Analytics
                </h1>
                <p className="text-[#A1A1AA] text-sm">Business performance insights</p>
              </div>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger data-testid="time-range-select" className="w-[140px] bg-[#121212] border-[#27272A] text-white">
                <SelectValue placeholder="Select range" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-[#27272A]">
                <SelectItem value="7days" className="text-white hover:bg-[#27272A]">Last 7 days</SelectItem>
                <SelectItem value="30days" className="text-white hover:bg-[#27272A]">Last 30 days</SelectItem>
                <SelectItem value="90days" className="text-white hover:bg-[#27272A]">Last 90 days</SelectItem>
                <SelectItem value="year" className="text-white hover:bg-[#27272A]">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </header>

        {/* Analytics Content */}
        <div className="p-4 lg:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* KPI Cards Row */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {/* Revenue Card */}
                <div data-testid="kpi-revenue" className="bg-[#121212] border border-[#39FF14] rounded-xl p-6 shadow-[0_0_15px_rgba(57,255,20,0.1)]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#39FF14] rounded-lg flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-black" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${analyticsData.revenue.change >= 0 ? 'text-[#39FF14]' : 'text-red-400'}`}>
                      {analyticsData.revenue.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {formatPercentage(analyticsData.revenue.change)}
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">Total Revenue</p>
                  <p className="text-3xl font-bold text-[#39FF14]">{formatCurrency(analyticsData.revenue.total)}</p>
                </div>

                {/* Jobs Card */}
                <div data-testid="kpi-jobs" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#064E3B] rounded-lg flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-[#39FF14]" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${analyticsData.jobs.change >= 0 ? 'text-[#39FF14]' : 'text-red-400'}`}>
                      {analyticsData.jobs.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {formatPercentage(analyticsData.jobs.change)}
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">Total Jobs</p>
                  <p className="text-3xl font-bold text-white">{analyticsData.jobs.total}</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    {analyticsData.jobs.completed} completed • {analyticsData.jobs.cancelled} cancelled
                  </p>
                </div>

                {/* Customers Card */}
                <div data-testid="kpi-customers" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#064E3B] rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#39FF14]" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${analyticsData.customers.change >= 0 ? 'text-[#39FF14]' : 'text-red-400'}`}>
                      {analyticsData.customers.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {formatPercentage(analyticsData.customers.change)}
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">Total Customers</p>
                  <p className="text-3xl font-bold text-white">{analyticsData.customers.total}</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    +{analyticsData.customers.new} new this period
                  </p>
                </div>

                {/* Quote Conversion Card */}
                <div data-testid="kpi-conversion" className="bg-[#121212] border border-[#27272A] rounded-xl p-6 hover:border-[#39FF14]/50 transition-colors">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 bg-[#064E3B] rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-[#39FF14]" />
                    </div>
                    <div className={`flex items-center gap-1 text-sm font-medium ${analyticsData.quotes.change >= 0 ? 'text-[#39FF14]' : 'text-red-400'}`}>
                      {analyticsData.quotes.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                      {formatPercentage(analyticsData.quotes.change)}
                    </div>
                  </div>
                  <p className="text-[#A1A1AA] text-sm uppercase tracking-wide mb-1">Quote Conversion</p>
                  <p className="text-3xl font-bold text-white">{analyticsData.quotes.conversionRate.toFixed(1)}%</p>
                  <p className="text-[#A1A1AA] text-xs mt-1">
                    {analyticsData.quotes.converted}/{analyticsData.quotes.total} quotes converted
                  </p>
                </div>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                {/* Revenue Trend Chart */}
                <div data-testid="revenue-chart" className="lg:col-span-2 bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="font-heading text-xl font-bold text-white uppercase">Revenue Trend</h2>
                      <p className="text-[#A1A1AA] text-sm">Monthly revenue over time</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-[#39FF14]"></div>
                      <span className="text-[#A1A1AA] text-sm">Revenue</span>
                    </div>
                  </div>
                  <SimpleBarChart data={analyticsData.revenueByMonth} />
                </div>

                {/* Subscription Breakdown */}
                {subscriptionsEnabled && <div data-testid="subscription-breakdown" className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">Subscriptions</h2>
                  <div className="flex items-center justify-center mb-6 relative">
                    <DonutProgress 
                      value={analyticsData.subscriptions.yearly} 
                      total={analyticsData.subscriptions.active || 1} 
                      color={JD_YELLOW}
                      size={100}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">{analyticsData.subscriptions.active}</span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#39FF14]"></div>
                        <span className="text-[#EDEDED]">Monthly</span>
                      </div>
                      <span className="text-white font-bold">{analyticsData.subscriptions.monthly}</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: JD_YELLOW }}></div>
                        <span className="text-[#EDEDED]">Yearly</span>
                      </div>
                      <span className="text-white font-bold">{analyticsData.subscriptions.yearly}</span>
                    </div>
                  </div>
                </div>}
              </div>

              {/* Bottom Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Customers */}
                <div data-testid="top-customers" className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="font-heading text-xl font-bold text-white uppercase">Top Customers</h2>
                    <Link to="/customers" className="text-[#39FF14] text-sm hover:underline">View All</Link>
                  </div>
                  {analyticsData.topCustomers.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-[#27272A] mx-auto mb-4" />
                      <p className="text-[#A1A1AA]">No customer data yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {analyticsData.topCustomers.map((customer, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-4 p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx === 0 ? 'bg-[#39FF14] text-black' : 'bg-[#27272A] text-white'
                          }`}>
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="text-white font-medium">{customer.name}</p>
                            <p className="text-[#A1A1AA] text-sm">{customer.jobCount} jobs completed</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Performance Metrics */}
                <div data-testid="performance-metrics" className="bg-[#121212] border border-[#27272A] rounded-xl p-6">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">Performance</h2>
                  <div className="space-y-6">
                    {/* Average Job Value */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#A1A1AA] text-sm">Average Job Value</span>
                        <span className="text-[#39FF14] font-bold">{formatCurrency(analyticsData.avgJobValue)}</span>
                      </div>
                      <div className="h-2 bg-[#27272A] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#39FF14] rounded-full transition-all duration-500"
                          style={{ width: `${Math.min((analyticsData.avgJobValue / 200) * 100, 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* Job Completion Rate */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#A1A1AA] text-sm">Job Completion Rate</span>
                        <span className="text-[#39FF14] font-bold">
                          {analyticsData.jobs.total > 0 
                            ? ((analyticsData.jobs.completed / analyticsData.jobs.total) * 100).toFixed(1) 
                            : 0}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#27272A] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#39FF14] rounded-full transition-all duration-500"
                          style={{ 
                            width: `${analyticsData.jobs.total > 0 
                              ? (analyticsData.jobs.completed / analyticsData.jobs.total) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    {/* Quote Response Rate */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#A1A1AA] text-sm">Quote Response Rate</span>
                        <span style={{ color: JD_YELLOW }} className="font-bold">
                          {analyticsData.quotes.total > 0 
                            ? (((analyticsData.quotesByStatus.sent || 0) + (analyticsData.quotesByStatus.converted || 0)) / analyticsData.quotes.total * 100).toFixed(1) 
                            : 0}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#27272A] rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            backgroundColor: JD_YELLOW,
                            width: `${analyticsData.quotes.total > 0 
                              ? (((analyticsData.quotesByStatus.sent || 0) + (analyticsData.quotesByStatus.converted || 0)) / analyticsData.quotes.total) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                    </div>

                    {/* Customer Retention (Subscription) */}
                    {subscriptionsEnabled && <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[#A1A1AA] text-sm">Subscription Rate</span>
                        <span className="text-blue-400 font-bold">
                          {analyticsData.customers.total > 0 
                            ? ((analyticsData.subscriptions.active / analyticsData.customers.total) * 100).toFixed(1) 
                            : 0}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#27272A] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-400 rounded-full transition-all duration-500"
                          style={{ 
                            width: `${analyticsData.customers.total > 0 
                              ? (analyticsData.subscriptions.active / analyticsData.customers.total) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                    </div>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Analytics;
